import React, { useState, useMemo, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faReceipt,
  faMoneyBill,
  faFilter,
  faEye,
  faSpinner,
  faCheckCircle,
  faClose,
  faBalanceScale,
  faWarehouse,
  faBoxes,
  faDollarSign,
  faTags,
  faShoppingCart,
  faUsers,
  faTruck,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
} from 'recharts';

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function DashboardContent() {
  const { stateNavigator } = useNavigationEvent();
  const userId = ls.get("currentuserid");

  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [chartPeriod, setChartPeriod] = useState("week");

  // SWR Data fetching
  const { data: salessales, mutate: mutatesales } = useSWR(
    `${process.env.REACT_APP_SALES_ORDER_API}?include=field_cashier,field_customer&sort=-created&page[limit]=1000`,
    fetcher,
  );
  const salessliced = salessales?.data ? salessales?.data.slice(0, 5) : salessales;
  
  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_customer_email,field_phone_number,field_customer_address,drupal_internal__nid&page[limit]=1000`,
    fetcher,
  );

  const { data: users } = useSWR(
    `${process.env.REACT_APP_USER_API}?&filter[drupal_internal__uid]=${userId}`,
    fetcher,
  );

  const { data: products } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_stock_quantity,field_price,field_cost_price&page[limit]=1000`,
    fetcher,
  );

  // Helper function to safely create date
  const safeDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const d = new Date(dateValue);
      return isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  };

  // Helper function to get week number
  const getWeekNumber = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const d = new Date(date);
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const days = Math.floor((d - yearStart) / (24 * 60 * 60 * 1000));
    return `${d.getFullYear()}-W${Math.ceil((days + 1) / 7)}`;
  };

  // Calculate comprehensive statistics - EXCLUDING Stock Movements
  const statistics = useMemo(() => {
    let salesStats = {
      totalRevenue: 0,
      totalSalesCount: 0,
      averageSaleAmount: 0,
      totalBalanceDue: 0,
      totalAmountPaid: 0,
      todayRevenue: 0,
      todaySalesCount: 0,
      salesByDay: [],
      weeklyData: [],
      monthlyData: [],
      salesByStatus: [],
      topProducts: [],
    };

    if (salessales?.data) {
      const today = new Date().toISOString().split("T")[0];
      const dayMap = {};
      const weekMap = {};
      const monthMap = {};
      const statusMap = {};
      const productSales = {};

      salessales.data.forEach((sale) => {
        const amount = parseFloat(sale.attributes.field_total_amount || 0);
        const paid = parseFloat(sale.attributes.field_total_paid || 0);
        const saleDate = safeDate(sale.attributes.created);
        
        if (!saleDate) return;

        const dateKey = saleDate.toISOString().split("T")[0];
        const weekKey = getWeekNumber(saleDate);
        const monthKey = saleDate.toISOString().slice(0, 7);
        const status = sale.attributes.field_payment_status || "pending";

        if (!dayMap[dateKey]) {
          dayMap[dateKey] = { date: dateKey, revenue: 0, count: 0 };
        }
        dayMap[dateKey].revenue += amount;
        dayMap[dateKey].count += 1;

        if (weekKey && !weekMap[weekKey]) {
          weekMap[weekKey] = { week: weekKey, revenue: 0, count: 0 };
        }
        if (weekKey) {
          weekMap[weekKey].revenue += amount;
          weekMap[weekKey].count += 1;
        }

        if (!monthMap[monthKey]) {
          monthMap[monthKey] = { month: monthKey, revenue: 0, count: 0 };
        }
        monthMap[monthKey].revenue += amount;
        monthMap[monthKey].count += 1;

        if (!statusMap[status]) {
          statusMap[status] = { name: status, value: 0 };
        }
        statusMap[status].value += amount;

        salesStats.totalRevenue += amount;
        salesStats.totalAmountPaid += paid;

        if (dateKey === today) {
          salesStats.todayRevenue += amount;
          salesStats.todaySalesCount++;
        }
      });

      salesStats.salesByDay = Object.values(dayMap)
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-30);

      salesStats.weeklyData = Object.values(weekMap)
        .filter(item => item.week)
        .sort((a, b) => a.week.localeCompare(b.week))
        .slice(-12);

      salesStats.monthlyData = Object.values(monthMap)
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);

      salesStats.salesByStatus = Object.values(statusMap);

      salesStats.totalSalesCount = salessales.data.length;
      salesStats.averageSaleAmount =
        salesStats.totalSalesCount > 0
          ? salesStats.totalRevenue / salesStats.totalSalesCount
          : 0;
      salesStats.totalBalanceDue =
        salesStats.totalRevenue - salesStats.totalAmountPaid;
    }

    // Inventory Statistics
    let inventoryStats = {
      totalProducts: 0,
      totalStockValue: 0,
      totalStockQuantity: 0,
      totalCostValue: 0,
      lowStockItems: 0,
      outOfStockItems: 0,
      inventoryTurnover: 0,
      stockStatusData: [],
    };

    if (products?.data) {
      let totalCost = 0;
      const statusCounts = { instock: 0, lowstock: 0, outofstock: 0 };
      
      products.data.forEach((product) => {
        const stock = parseInt(product.attributes.field_stock_quantity) || 0;
        const price = parseFloat(product.attributes.field_price) || 0;
        const cost = parseFloat(product.attributes.field_cost_price) || 0;

        inventoryStats.totalProducts++;
        inventoryStats.totalStockQuantity += stock;
        inventoryStats.totalStockValue += stock * price;
        totalCost += stock * cost;

        if (stock === 0) {
          inventoryStats.outOfStockItems++;
          statusCounts.outofstock++;
        } else if (stock <= 10) {
          inventoryStats.lowStockItems++;
          statusCounts.lowstock++;
        } else {
          statusCounts.instock++;
        }
      });

      inventoryStats.totalCostValue = totalCost;
      inventoryStats.stockStatusData = [
        { name: 'In Stock', value: statusCounts.instock },
        { name: 'Low Stock', value: statusCounts.lowstock },
        { name: 'Out of Stock', value: statusCounts.outofstock },
      ];

      if (inventoryStats.totalCostValue > 0 && salesStats.totalRevenue > 0) {
        inventoryStats.inventoryTurnover = (
          salesStats.totalRevenue / inventoryStats.totalCostValue
        ).toFixed(2);
      }
    }

    // Customer Statistics
    let customerStats = {
      totalCustomers: 0,
      activeCustomers: 0,
      newCustomersThisMonth: 0,
      customersByMonth: [],
    };

    if (customers?.data) {
      customerStats.totalCustomers = customers.data.length;
      customerStats.activeCustomers = customers.data.length;

      const monthMap = {};
      customers.data.forEach((customer) => {
        const createdDate = safeDate(customer.attributes?.created);
        if (!createdDate) return;
        
        const monthKey = createdDate.toISOString().slice(0, 7);
        if (!monthMap[monthKey]) {
          monthMap[monthKey] = { month: monthKey, count: 0 };
        }
        monthMap[monthKey].count += 1;
      });

      customerStats.customersByMonth = Object.values(monthMap)
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);
    }

    return {
      sales: salesStats,
      inventory: inventoryStats,
      customers: customerStats,
      profitMargin:
        salesStats.totalRevenue > 0
          ? (
              ((salesStats.totalRevenue - inventoryStats.totalCostValue) /
                salesStats.totalRevenue) *
              100
            ).toFixed(1)
          : 0,
      stockCoverage:
        salesStats.averageSaleAmount > 0
          ? (
              inventoryStats.totalStockValue / salesStats.averageSaleAmount
            ).toFixed(1)
          : 0,
    };
  }, [salessales, products, customers]);

  const { sales, inventory, customers: customerStats } = statistics;

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  const STATUS_COLORS = ['#4CAF50', '#FF9800', '#F44336'];

  // Helper functions
  const calculateSaleBalance = (sale) => {
    const total = parseFloat(sale.attributes.field_total_amount || 0);
    const paid = parseFloat(sale.attributes.field_total_paid || 0);
    return total - paid;
  };

  const getBalanceDisplay = (sale) => {
    const balance = calculateSaleBalance(sale);

    if (balance > 0) {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#d32f2f",
        status: "Due",
      };
    } else if (balance < 0) {
      return {
        formatted: `GH₵${Math.abs(balance).toFixed(2)}`,
        color: "#ff9800",
        status: "Credit",
      };
    } else {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#388e3c",
        status: "Paid",
      };
    }
  };

  const handleDelete = async (salesId) => {
    try {
      const freshToken = await getCsrfToken();

      if (!freshToken) {
        setErrorMessage("Unable to authenticate. Please refresh the page.");
        setModalError(true);
        return;
      }

      if (window.confirm("Are you sure you want to delete this sale?")) {
        const res = await fetch(
          `${process.env.REACT_APP_SALES_ORDER_API}/${salesId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": freshToken,
            },
            credentials: "include",
          },
        );

        if (res.ok) {
          setModalSuccessMsg(true);
          mutatesales();
        } else {
          setErrorMessage("Failed to delete sale");
          setModalError(true);
        }
      }
    } catch (error) {
      setErrorMessage("Network error");
      setModalError(true);
    }
  };

  const getCashierName = (sale) => {
    const cashierId = sale.relationships?.field_cashier?.data?.id;
    if (!cashierId || !users?.data) return "N/A";
    const user = users.data.find((u) => u.id === cashierId);
    return user?.attributes?.name || "N/A";
  };

  const getCustomerName = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.title || "N/A";
  };

  const getCustomerPhoneNumber = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.field_phone_number || "N/A";
  };

  // Get chart data based on selected period
  const getChartData = () => {
    switch (chartPeriod) {
      case 'week':
        return sales.salesByDay.slice(-7);
      case 'month':
        return sales.salesByDay.slice(-30);
      case 'quarter':
        return sales.weeklyData.slice(-13);
      default:
        return sales.salesByDay.slice(-7);
    }
  };

  const chartData = getChartData();

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={styles.customTooltip}>
          <p style={styles.tooltipLabel}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '5px 0' }}>
              {entry.name}: {typeof entry.value === 'number' ? `GH₵${entry.value.toFixed(2)}` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="dashboard-main" style={styles.container}>
          <TopActionMenus
            AddTitle="Dashboard"
            buttonLabel="New Sale"
            onClick={() => stateNavigator.navigate("salesorder")}
          />

          {/* CHART SECTION */}
          <div style={styles.chartSection}>
            <div style={styles.chartHeader}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faChartLine} /> Sales Analytics
              </h3>
              <div style={styles.periodSelector}>
                <button
                  style={{
                    ...styles.periodButton,
                    ...(chartPeriod === 'week' ? styles.activePeriodButton : {})
                  }}
                  onClick={() => setChartPeriod('week')}
                >
                  Week
                </button>
                <button
                  style={{
                    ...styles.periodButton,
                    ...(chartPeriod === 'month' ? styles.activePeriodButton : {})
                  }}
                  onClick={() => setChartPeriod('month')}
                >
                  Month
                </button>
                <button
                  style={{
                    ...styles.periodButton,
                    ...(chartPeriod === 'quarter' ? styles.activePeriodButton : {})
                  }}
                  onClick={() => setChartPeriod('quarter')}
                >
                  Quarter
                </button>
              </div>
            </div>

            <div style={styles.chartsGrid}>
              {/* Revenue Trend Chart */}
              <div style={styles.chartCard}>
                <h4 style={styles.chartTitle}>Revenue Trend</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={chartData.length > 0 ? chartData : [{ date: 'No Data', revenue: 0, count: 0 }]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={chartPeriod === 'week' || chartPeriod === 'month' ? 'date' : 'week'} 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      tickFormatter={(value) => `GH₵${value}`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      fill="#0088FE"
                      stroke="#0088FE"
                      fillOpacity={0.3}
                      name="Revenue"
                    />
                    <Bar
                      dataKey="count"
                      barSize={30}
                      fill="#00C49F"
                      name="Sales Count"
                      yAxisId="right"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Sales Status Distribution */}
              <div style={styles.chartCard}>
                <h4 style={styles.chartTitle}>Sales Distribution by Status</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sales.salesByStatus.length > 0 ? sales.salesByStatus : [{ name: 'No Data', value: 1 }]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sales.salesByStatus.length > 0 ? (
                        sales.salesByStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))
                      ) : (
                        <Cell fill="#e0e0e0" />
                      )}
                    </Pie>
                    <Tooltip formatter={(value) => `GH₵${value.toFixed(2)}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Inventory Status Chart */}
              <div style={styles.chartCard}>
                <h4 style={styles.chartTitle}>Inventory Status</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={inventory.stockStatusData.length > 0 ? inventory.stockStatusData : [{ name: 'No Data', value: 1 }]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {inventory.stockStatusData.length > 0 ? (
                        inventory.stockStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                        ))
                      ) : (
                        <Cell fill="#e0e0e0" />
                      )}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <div style={styles.inventoryAlerts}>
                  {inventory.outOfStockItems > 0 && (
                    <div style={{ ...styles.alertBadge, background: '#F44336' }}>
                      ⚠️ {inventory.outOfStockItems} Out of Stock
                    </div>
                  )}
                  {inventory.lowStockItems > 0 && (
                    <div style={{ ...styles.alertBadge, background: '#FF9800' }}>
                      ⚠️ {inventory.lowStockItems} Low Stock
                    </div>
                  )}
                  {inventory.outOfStockItems === 0 && inventory.lowStockItems === 0 && (
                    <div style={{ ...styles.alertBadge, background: '#4CAF50' }}>
                      ✅ All items in stock
                    </div>
                  )}
                </div>
              </div>

              {/* Monthly Revenue Trend */}
              <div style={styles.chartCard}>
                <h4 style={styles.chartTitle}>Monthly Revenue</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sales.monthlyData.length > 0 ? sales.monthlyData : [{ month: 'No Data', revenue: 0, count: 0 }]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={(value) => `GH₵${value}`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8884D8" name="Revenue" />
                    <Bar dataKey="count" fill="#82ca9d" name="Sales Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Customer Growth */}
              <div style={styles.chartCard}>
                <h4 style={styles.chartTitle}>Customer Growth</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={customerStats.customersByMonth.length > 0 ? customerStats.customersByMonth : [{ month: 'No Data', count: 0 }]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#FF8042"
                      fill="#FF8042"
                      fillOpacity={0.3}
                      name="New Customers"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* RECENT SALES TABLE */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faReceipt} /> Recent Sales
              </h3>
            </div>

            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Contact</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!salessliced ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        sales...
                      </td>
                    </tr>
                  ) : salessliced.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    salessliced.map((sale) => {
                      const balanceDisplay = getBalanceDisplay(sale);
                      return (
                        <tr key={sale.id}>
                          <td style={styles.td}>
                            {sale.attributes.field_order_number}
                          </td>
                          <td style={styles.td}>
                            {new Date(
                              sale.attributes.created,
                            ).toLocaleDateString()}
                          </td>
                          <td style={styles.td}>{getCustomerName(sale)}</td>
                          <td style={styles.td}>
                            {getCustomerPhoneNumber(sale)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_amount,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_paid || 0,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                color: balanceDisplay.color,
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              {balanceDisplay.formatted}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <button
                                style={styles.viewButton}
                                onClick={() =>
                                  stateNavigator.navigate(
                                    "viewsalesinvoicedetails",
                                    {
                                      salesId: sale.id,
                                    },
                                  )
                                }
                                title="View Invoice"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                style={styles.editButton}
                                onClick={() =>
                                  stateNavigator.navigate("editsalesorder", {
                                    salesId: sale.id,
                                  })
                                }
                                title="Edit Sale"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                style={styles.deleteButton}
                                onClick={() => handleDelete(sale.id)}
                                title="Delete Sale"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="Sale deleted successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faFilter}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  sectionTitle: {
    margin: "0",
    color: "#333",
    fontSize: "18px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  chartSection: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    marginTop: "20px",
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px",
  },
  periodSelector: {
    display: "flex",
    gap: "8px",
  },
  periodButton: {
    padding: "6px 16px",
    borderRadius: "20px",
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    ":hover": {
      background: "#f8f9fa",
    },
  },
  activePeriodButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
    gap: "20px",
  },
  chartCard: {
    background: "#fafafa",
    padding: "15px",
    borderRadius: "8px",
    border: "1px solid #e8e8e8",
  },
  chartTitle: {
    margin: "0 0 15px 0",
    fontSize: "14px",
    fontWeight: "600",
    color: "#555",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "10px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid #eee",
    fontSize: "14px",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    ":hover": {
      backgroundColor: "#bbdefb",
    },
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    ":hover": {
      backgroundColor: "#c8e6c9",
    },
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    ":hover": {
      backgroundColor: "#ffcdd2",
    },
  },
  customTooltip: {
    background: "white",
    padding: "12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  tooltipLabel: {
    margin: "0 0 8px 0",
    fontWeight: "bold",
    fontSize: "14px",
  },
  statSummaryCard: {
    background: "#fafafa",
    padding: "15px",
    borderRadius: "8px",
    border: "1px solid #e8e8e8",
    gridColumn: "1 / -1",
  },
  statSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "15px",
  },
  statSummaryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px",
    background: "white",
    borderRadius: "6px",
    border: "1px solid #eee",
  },
  statSummaryLabel: {
    fontSize: "12px",
    color: "#888",
    fontWeight: "500",
  },
  statSummaryValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#333",
    marginTop: "4px",
  },
  inventoryAlerts: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "10px",
    flexWrap: "wrap",
  },
  alertBadge: {
    padding: "6px 14px",
    borderRadius: "20px",
    color: "white",
    fontSize: "13px",
    fontWeight: "600",
  },
};